/**
 * Catalogue curé d'icônes Tabler pour le picker (IconPickerField) :
 * sous-ensemble métier (~120 icônes) avec mots-clés FRANÇAIS — la recherche
 * du pack complet ne matche que les noms anglais. La grille par défaut du
 * picker affiche ce catalogue ; la recherche matche mots-clés FR + nom
 * anglais, puis complète avec le pack Tabler complet.
 */

import * as TablerIcons from '@tabler/icons-react';

/** Normalisation recherche : minuscules + accents retirés. */
export function normalizeIconQuery(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

export const ICON_CATALOG = [
  // Personnes & entreprise
  { name: 'IconUser', keywords: ['personne', 'utilisateur', 'profil', 'contact'] },
  { name: 'IconUsers', keywords: ['clients', 'equipe', 'personnes', 'groupe', 'utilisateurs'] },
  { name: 'IconUsersGroup', keywords: ['equipe', 'communaute', 'groupe', 'collaborateurs'] },
  { name: 'IconUserCheck', keywords: ['client valide', 'recrutement', 'confiance'] },
  { name: 'IconUserPlus', keywords: ['nouveau client', 'inscription', 'ajouter personne'] },
  { name: 'IconBuilding', keywords: ['entreprise', 'societe', 'immeuble', 'bureau'] },
  { name: 'IconBuildingSkyscraper', keywords: ['siege', 'grande entreprise', 'tour', 'ville'] },
  { name: 'IconBriefcase', keywords: ['travail', 'metier', 'business', 'mallette', 'emploi'] },
  { name: 'IconHeartHandshake', keywords: ['partenariat', 'accord', 'confiance', 'engagement'] },
  { name: 'IconHandClick', keywords: ['clic', 'interaction', 'choix'] },

  // Croissance & statistiques
  { name: 'IconChartBar', keywords: ['statistiques', 'graphique', 'barres', 'resultats'] },
  { name: 'IconChartLine', keywords: ['courbe', 'evolution', 'tendance', 'progression'] },
  { name: 'IconChartPie', keywords: ['camembert', 'repartition', 'parts', 'pourcentage'] },
  { name: 'IconTrendingUp', keywords: ['croissance', 'hausse', 'progression', 'augmentation'] },
  { name: 'IconTrendingDown', keywords: ['baisse', 'diminution', 'reduction'] },
  { name: 'IconPercentage', keywords: ['pourcentage', 'taux', 'remise'] },
  { name: 'IconTargetArrow', keywords: ['objectif', 'cible', 'but', 'precision'] },
  { name: 'IconAward', keywords: ['recompense', 'prix', 'medaille', 'qualite', 'certification'] },
  { name: 'IconTrophy', keywords: ['trophee', 'victoire', 'gagnant', 'succes', 'premier'] },
  { name: 'IconRocket', keywords: ['fusee', 'lancement', 'demarrage', 'croissance', 'startup'] },

  // Temps & organisation
  { name: 'IconClock', keywords: ['horloge', 'heure', 'temps', 'delai', 'disponibilite'] },
  { name: 'IconClockHour4', keywords: ['horloge', 'heure', 'ponctualite'] },
  { name: 'IconCalendar', keywords: ['calendrier', 'date', 'planning', 'rendez-vous'] },
  { name: 'IconCalendarCheck', keywords: ['rendez-vous confirme', 'echeance', 'date validee'] },
  { name: 'IconHourglass', keywords: ['sablier', 'attente', 'duree'] },
  { name: 'IconHistory', keywords: ['historique', 'passe', 'anciennete', 'experience'] },
  { name: 'IconChecklist', keywords: ['liste', 'taches', 'etapes', 'suivi'] },
  { name: 'IconListCheck', keywords: ['liste cochee', 'validation', 'controle'] },

  // Validation & sécurité
  { name: 'IconCheck', keywords: ['valide', 'coche', 'ok', 'fait'] },
  { name: 'IconCircleCheck', keywords: ['valide', 'confirme', 'garanti', 'succes'] },
  { name: 'IconShield', keywords: ['bouclier', 'securite', 'protection'] },
  { name: 'IconShieldCheck', keywords: ['securite validee', 'protection', 'garantie', 'conformite'] },
  { name: 'IconLock', keywords: ['cadenas', 'securise', 'confidentiel', 'verrouille'] },
  { name: 'IconKey', keywords: ['cle', 'acces', 'solution'] },
  { name: 'IconCertificate', keywords: ['certificat', 'diplome', 'certification', 'norme'] },
  { name: 'IconThumbUp', keywords: ['pouce', 'satisfaction', 'approbation', 'avis positif', 'like'] },
  { name: 'IconStar', keywords: ['etoile', 'note', 'avis', 'favori', 'qualite'] },
  { name: 'IconHeart', keywords: ['coeur', 'satisfaction', 'fidelite', 'aime'] },

  // Communication & support
  { name: 'IconPhone', keywords: ['telephone', 'appel', 'contact'] },
  { name: 'IconMail', keywords: ['email', 'courriel', 'message', 'contact'] },
  { name: 'IconMessage', keywords: ['message', 'discussion', 'chat', 'echange'] },
  { name: 'IconMessages', keywords: ['conversations', 'support', 'echanges'] },
  { name: 'IconHeadset', keywords: ['support', 'assistance', 'service client', 'casque', 'hotline'] },
  { name: 'IconBell', keywords: ['cloche', 'notification', 'alerte', 'rappel'] },
  { name: 'IconSpeakerphone', keywords: ['megaphone', 'annonce', 'communication', 'marketing'] },
  { name: 'IconShare', keywords: ['partage', 'diffusion', 'reseaux'] },

  // Commerce & finance
  { name: 'IconShoppingCart', keywords: ['panier', 'achat', 'commande', 'ecommerce'] },
  { name: 'IconCreditCard', keywords: ['carte bancaire', 'paiement', 'transaction'] },
  { name: 'IconCash', keywords: ['argent', 'especes', 'billets', 'economies'] },
  { name: 'IconCoins', keywords: ['pieces', 'monnaie', 'economies', 'finance'] },
  { name: 'IconCurrencyEuro', keywords: ['euro', 'prix', 'tarif', 'cout'] },
  { name: 'IconReceipt', keywords: ['facture', 'recu', 'ticket', 'devis'] },
  { name: 'IconDiscount', keywords: ['remise', 'promotion', 'reduction', 'solde'] },
  { name: 'IconGift', keywords: ['cadeau', 'offre', 'bonus', 'avantage'] },
  { name: 'IconTag', keywords: ['etiquette', 'prix', 'categorie'] },
  { name: 'IconWallet', keywords: ['portefeuille', 'budget', 'paiement'] },

  // Technologie & outils
  { name: 'IconDeviceLaptop', keywords: ['ordinateur', 'portable', 'informatique', 'numerique'] },
  { name: 'IconDeviceMobile', keywords: ['telephone', 'mobile', 'smartphone', 'application'] },
  { name: 'IconWorld', keywords: ['monde', 'international', 'planete', 'web', 'global'] },
  { name: 'IconCloud', keywords: ['nuage', 'cloud', 'stockage', 'en ligne'] },
  { name: 'IconDatabase', keywords: ['base de donnees', 'stockage', 'donnees'] },
  { name: 'IconServer', keywords: ['serveur', 'hebergement', 'infrastructure'] },
  { name: 'IconCode', keywords: ['code', 'developpement', 'programmation'] },
  { name: 'IconSettings', keywords: ['reglages', 'parametres', 'engrenage', 'configuration'] },
  { name: 'IconTool', keywords: ['outil', 'cle', 'maintenance', 'reparation'] },
  { name: 'IconTools', keywords: ['outils', 'bricolage', 'intervention', 'chantier'] },
  { name: 'IconRobot', keywords: ['robot', 'automatisation', 'intelligence artificielle', 'ia'] },
  { name: 'IconCpu', keywords: ['processeur', 'puce', 'technologie', 'performance'] },
  { name: 'IconWifi', keywords: ['wifi', 'connexion', 'reseau', 'internet'] },
  { name: 'IconBolt', keywords: ['eclair', 'rapidite', 'energie', 'vitesse', 'instantane'] },
  { name: 'IconBulb', keywords: ['ampoule', 'idee', 'innovation', 'creativite', 'conseil'] },

  // Documents & fichiers
  { name: 'IconFile', keywords: ['fichier', 'document', 'page'] },
  { name: 'IconFileText', keywords: ['document texte', 'rapport', 'contrat'] },
  { name: 'IconFileCheck', keywords: ['document valide', 'contrat signe', 'dossier complet'] },
  { name: 'IconFolder', keywords: ['dossier', 'classement', 'projets'] },
  { name: 'IconClipboard', keywords: ['presse-papiers', 'formulaire', 'inventaire'] },
  { name: 'IconBook', keywords: ['livre', 'documentation', 'formation', 'guide'] },
  { name: 'IconPencil', keywords: ['crayon', 'redaction', 'modification', 'signature'] },
  { name: 'IconEdit', keywords: ['edition', 'modifier', 'personnalisation'] },
  { name: 'IconSearch', keywords: ['loupe', 'recherche', 'analyse', 'audit'] },
  { name: 'IconEye', keywords: ['oeil', 'visibilite', 'vue', 'surveillance'] },

  // Lieux & logistique
  { name: 'IconMapPin', keywords: ['localisation', 'adresse', 'carte', 'lieu', 'agence'] },
  { name: 'IconMap', keywords: ['carte', 'zone', 'territoire', 'couverture'] },
  { name: 'IconHome', keywords: ['maison', 'accueil', 'domicile', 'immobilier'] },
  { name: 'IconTruck', keywords: ['camion', 'livraison', 'transport', 'logistique'] },
  { name: 'IconPackage', keywords: ['colis', 'paquet', 'expedition', 'produit'] },
  { name: 'IconPlane', keywords: ['avion', 'voyage', 'international', 'export'] },
  { name: 'IconCar', keywords: ['voiture', 'vehicule', 'deplacement', 'automobile'] },
  { name: 'IconBuildingStore', keywords: ['boutique', 'magasin', 'commerce', 'point de vente'] },
  { name: 'IconBuildingFactory2', keywords: ['usine', 'production', 'industrie', 'fabrication'] },

  // Nature & divers
  { name: 'IconLeaf', keywords: ['feuille', 'ecologie', 'nature', 'durable', 'environnement'] },
  { name: 'IconRecycle', keywords: ['recyclage', 'ecologie', 'economie circulaire'] },
  { name: 'IconSun', keywords: ['soleil', 'energie solaire', 'ete', 'lumiere'] },
  { name: 'IconDroplet', keywords: ['goutte', 'eau', 'liquide', 'hydraulique'] },
  { name: 'IconFlame', keywords: ['flamme', 'feu', 'chauffage', 'energie', 'populaire'] },
  { name: 'IconPlant', keywords: ['plante', 'croissance', 'jardin', 'agriculture'] },
  { name: 'IconPaw', keywords: ['patte', 'animaux', 'veterinaire'] },
  { name: 'IconMoodSmile', keywords: ['sourire', 'satisfaction', 'bonheur', 'bien-etre'] },
  { name: 'IconConfetti', keywords: ['confettis', 'fete', 'celebration', 'evenement'] },
  { name: 'IconSparkles', keywords: ['etincelles', 'nouveaute', 'magie', 'premium', 'brillant'] },
  { name: 'IconDiamond', keywords: ['diamant', 'luxe', 'premium', 'valeur'] },
  { name: 'IconCrown', keywords: ['couronne', 'premium', 'leader', 'meilleur'] },
  { name: 'IconFlag', keywords: ['drapeau', 'objectif', 'etape', 'jalon'] },
  { name: 'IconCompass', keywords: ['boussole', 'orientation', 'direction', 'strategie'] },
  { name: 'IconPuzzle', keywords: ['puzzle', 'solution', 'integration', 'modularite'] },
  { name: 'IconInfinity', keywords: ['infini', 'illimite', 'sans limite'] },
  { name: 'IconScale', keywords: ['balance', 'justice', 'juridique', 'equilibre', 'droit'] },
  { name: 'IconStethoscope', keywords: ['stethoscope', 'sante', 'medical', 'medecin'] },
  { name: 'IconSchool', keywords: ['ecole', 'formation', 'education', 'apprentissage'] },
  { name: 'IconMicrophone', keywords: ['micro', 'podcast', 'conference', 'voix'] },
  { name: 'IconCamera', keywords: ['appareil photo', 'photographie', 'image'] },
  { name: 'IconMusic', keywords: ['musique', 'note', 'audio', 'son'] },
  { name: 'IconPalette', keywords: ['palette', 'couleurs', 'design', 'creation', 'art'] },
];

const ICON_NAMES = Object.keys(TablerIcons).filter((k) => k.startsWith('Icon'));

/** Icônes du catalogue réellement présentes dans le pack installé. */
const CATALOG = ICON_CATALOG.filter((entry) => TablerIcons[entry.name]);
const CATALOG_NAMES = CATALOG.map((entry) => entry.name);
const CATALOG_NAME_SET = new Set(CATALOG_NAMES);

const MAX_RESULTS = 60;

/**
 * Recherche : mots-clés FRANÇAIS du catalogue curé (accents ignorés) + nom
 * anglais, puis complète avec le pack Tabler complet. Sans requête : le
 * catalogue curé entier (ordre thématique).
 *
 * Vit ici plutôt que dans le picker : le configurateur du document propose les
 * mêmes icônes, et n'a pas à embarquer une UI Mantine pour ça.
 */
export function searchIcons(query) {
  const q = normalizeIconQuery(query);
  if (!q) return CATALOG_NAMES;

  const curated = CATALOG.filter(
    (entry) => entry.keywords.some((k) => k.includes(q))
      || entry.name.toLowerCase().includes(q),
  ).map((entry) => entry.name);

  const qCompact = q.replace(/\s+/g, '');
  const rest = ICON_NAMES.filter(
    (name) => !CATALOG_NAME_SET.has(name) && name.toLowerCase().includes(qCompact),
  );

  return [...curated, ...rest].slice(0, MAX_RESULTS);
}
